import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { includes } from 'lodash';
import { Formik, useFormikContext } from 'formik';
import * as yup from 'yup';
import {
  AccountData,
  OrderData,
  OrderPageTypes,
  OrderService,
  VoucherOut,
  VoucherType,
  WaitingListItemData,
} from '../../../../api';
import { useCalendar } from '../../../CalendarContext/CalendarContext';
import { CalendarView, DATE_FORMAT } from '../../../calendarWrapper/CalendarWrapper';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { useAssignmentConflictHandler } from '../../../../common/apiNotifications/responseHandlers/useAssignmentConflictHandler';
import { EditOrderDialogIntent } from '../components/footer/EditOrderDialogFooter';
import { EditOrderFormValues } from '../EditOrderTypes';
// @ts-ignore
import { useEditOrder } from '../EditOrderContext';
import { useEditOrderDates } from '../hooks/useEditOrderDates';
import { useVoucherDialogs } from '../hooks/useVoucherDialogs';
import { getInitialOrder } from '../orderForm/orderFormDefaults';
import { buildOrderFormInitialValues } from '../orderForm/orderFormValues';
import { buildOrderValidationSchema } from '../orderForm/orderValidation';
import { buildCreateOrderPayload } from '../orderForm/createOrderPayload';
import { OrderFormSync } from '../orderForm/OrderFormSync';
import { WizardStepper } from './WizardStepper';
import { IntentToggle } from '../components/IntentToggle';
import { WizardFooter } from './WizardFooter';
import { WhatWhenStep } from './steps/WhatWhenStep';
import { CustomerStep } from './steps/CustomerStep';
import { ExtrasStep } from './steps/ExtrasStep';
import { defaultUpfront, depositPriceToSend, PaymentModel } from './components/paymentModel';
import { usePaymentModel } from './components/usePaymentModel';
import {
  firstIncompleteStep,
  stepOfField,
  StepOneError,
  validateStepOne,
  validateStepThree,
  WizardStep,
} from './wizardSteps';

type CreateOrderWizardProps = {
  editOrderDialogIntent: EditOrderDialogIntent;
  editOrderDialogState: any;
  closeEditOrderDialog: any;
  addOrder?: (order: OrderData) => Promise<void>;
  voucherToRedeem?: VoucherOut;
  redeemVoucherCallback?: (voucher: VoucherOut) => void;
  waitingListItem?: WaitingListItemData;
  cloneSourceOrder?: OrderData;
  canToggleIntent?: boolean;
  onToggleIntent?: (intent: EditOrderDialogIntent) => void;
};

const failingFieldNames = (validationSchema: yup.AnySchema, values: EditOrderFormValues): string[] => {
  try {
    validationSchema.validateSync(values, { abortEarly: false });
    return [];
  } catch (error) {
    return (error as yup.ValidationError).inner.map((validationError) => validationError.path ?? '');
  }
};

type CreateOrderWizardBodyProps = Omit<CreateOrderWizardProps, 'addOrder' | 'redeemVoucherCallback'> & {
  initialStep: WizardStep;
  isEventOrCourse: boolean;
  availableDatesForEvents: ReturnType<typeof useEditOrderDates>['availableDatesForEvents'];
  isLoadingDates: boolean;
  isWaitlistItem: boolean;
  loading: boolean;
  voucherDialogNodes: React.ReactNode;
};

const CreateOrderWizardBody = ({
  editOrderDialogIntent,
  editOrderDialogState,
  voucherToRedeem,
  cloneSourceOrder,
  canToggleIntent,
  onToggleIntent,
  initialStep,
  isEventOrCourse,
  availableDatesForEvents,
  isLoadingDates,
  isWaitlistItem,
  loading,
  voucherDialogNodes,
}: CreateOrderWizardBodyProps) => {
  const { values, validateForm, setFieldTouched, setFieldValue, submitForm } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState } = useEditOrder();
  const [step, setStep] = useState<WizardStep>(initialStep);
  const [stepErrors, setStepErrors] = useState<Set<WizardStep>>(new Set());
  const [failedCreateStep, setFailedCreateStep] = useState<WizardStep | undefined>(undefined);

  // A payment link can be sent for a new order, a converted waiting list item or a monetary voucher redeem.
  const showPaymentLinks =
    [EditOrderDialogIntent.CreateOrder, EditOrderDialogIntent.ConvertWaitListItem].includes(editOrderDialogIntent) ||
    voucherToRedeem?.type === VoucherType.MONETARY;

  const isVoucherRedeem = !!voucherToRedeem;
  const { model: paymentModel, deposit } = usePaymentModel(isVoucherRedeem);
  const orderPrice: number = editOrderState.details.price;
  const orderPageId: string | undefined = editOrderState.orderPage?.id;

  // A new payment model or order page starts from its own default again, dropping an amount edited for the old one.
  useEffect(() => {
    void setFieldValue('upfrontEdited', false);
  }, [paymentModel, orderPageId]);

  // The up-front amount follows its default (persons, price, link) until staff edit it. With a payment link on a
  // pay-at-the-visit order, the default is the whole price: that is what the link then asks for.
  useEffect(() => {
    if (values.upfrontEdited) {
      return;
    }
    const upfrontDefault =
      paymentModel === PaymentModel.PayAtVisit && values.createPaymentLink
        ? orderPrice
        : defaultUpfront({ model: paymentModel, deposit, orderPrice });
    void setFieldValue('upfrontAmount', upfrontDefault);
  }, [values.upfrontEdited, values.createPaymentLink, paymentModel, deposit, orderPrice]);

  const stepOneContext = { isEventOrCourse, availableDatesForEvents };
  // A voucher redeem has no up-front amount to check: a monetary one sends its remainder by link.
  const stepThreeError = isVoucherRedeem
    ? undefined
    : validateStepThree({
        createPaymentLink: values.createPaymentLink,
        upfrontAmount: values.upfrontAmount,
        model: paymentModel,
        orderPrice,
      });
  const liveStepOneError = stepErrors.has(WizardStep.WhatWhen) ? validateStepOne(values, stepOneContext) : undefined;

  const markStepErrors = (failingSteps: WizardStep[]) => {
    setStepErrors((previous) => {
      const next = new Set(previous);
      [WizardStep.WhatWhen, WizardStep.Customer, WizardStep.Extras].forEach((item) => {
        if (failingSteps.includes(item)) {
          next.add(item);
        } else {
          next.delete(item);
        }
      });
      return next;
    });
  };

  const setStepHasError = (stepToMark: WizardStep, hasError: boolean) => {
    setStepErrors((previous) => {
      const next = new Set(previous);
      if (hasError) {
        next.add(stepToMark);
      } else {
        next.delete(stepToMark);
      }
      return next;
    });
  };

  useEffect(() => {
    if (stepErrors.has(WizardStep.Extras) && !stepThreeError) {
      setStepHasError(WizardStep.Extras, false);
    }
  }, [stepThreeError]);

  const customerErrorFields = async (): Promise<string[]> => {
    const formErrors = await validateForm();
    const fields = Object.keys(formErrors).filter((field) => stepOfField(field) === WizardStep.Customer);
    fields.forEach((field) => {
      void setFieldTouched(field, true, false);
    });
    return fields;
  };

  const validateStep = async (stepToValidate: WizardStep): Promise<boolean> => {
    if (stepToValidate === WizardStep.WhatWhen) {
      const hasError = !!validateStepOne(values, stepOneContext);
      setStepHasError(WizardStep.WhatWhen, hasError);
      return !hasError;
    }
    if (stepToValidate === WizardStep.Customer) {
      const hasError = (await customerErrorFields()).length > 0;
      setStepHasError(WizardStep.Customer, hasError);
      return !hasError;
    }
    return true;
  };

  const goToFirstFailingStep = async (): Promise<boolean> => {
    const stepOneError = validateStepOne(values, stepOneContext);
    const customerErrors = await customerErrorFields();
    const failingSteps = [
      ...(stepOneError ? [WizardStep.WhatWhen] : []),
      ...(customerErrors.length > 0 ? [WizardStep.Customer] : []),
      ...(stepThreeError ? [WizardStep.Extras] : []),
    ];
    markStepErrors(failingSteps);
    if (failingSteps.length === 0) {
      setFailedCreateStep(undefined);
      return true;
    }
    const firstFailingStep = firstIncompleteStep({ stepOneError, customerErrors });
    setFailedCreateStep(firstFailingStep);
    setStep(firstFailingStep);
    return false;
  };

  const handleNext = async () => {
    if (await validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleCreate = async () => {
    if (await goToFirstFailingStep()) {
      await submitForm();
    }
  };

  const renderStep = () => {
    switch (step) {
      case WizardStep.WhatWhen:
        return (
          <WhatWhenStep
            editOrderDialogIntent={editOrderDialogIntent}
            cloneSourceOrder={cloneSourceOrder}
            voucherToRedeem={voucherToRedeem}
            availableDatesForEvents={availableDatesForEvents}
            isLoadingDates={isLoadingDates}
            stepOneError={liveStepOneError}
          />
        );
      case WizardStep.Customer:
        return <CustomerStep />;
      default:
        return (
          <ExtrasStep
            voucherToRedeem={voucherToRedeem}
            showPaymentLinks={showPaymentLinks}
            showLinkAmountError={stepErrors.has(WizardStep.Extras)}
          />
        );
    }
  };

  return (
    <>
      <OrderFormSync
        isEditOrder={false}
        isEventOrCourse={isEventOrCourse}
        isWaitlistItem={isWaitlistItem}
        isClone={!!cloneSourceOrder}
        hasParent={!!editOrderDialogState.parent}
        availableDatesForEvents={availableDatesForEvents}
      />
      {canToggleIntent && step === WizardStep.WhatWhen && (
        <IntentToggle editOrderDialogIntent={editOrderDialogIntent} onToggleIntent={onToggleIntent} />
      )}
      <WizardStepper step={step} stepErrors={stepErrors} onSelect={setStep} />
      <Grid
        container
        flexDirection="column"
        flexWrap="nowrap"
        sx={{
          width: 'auto',
          marginInline: '-4px',
          paddingInline: '4px',
          paddingBlockStart: '4px',
          marginBlockStart: '-4px',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
        }}>
        {renderStep()}
      </Grid>
      <WizardFooter
        step={step}
        loading={loading}
        editOrderDialogIntent={editOrderDialogIntent}
        paymentModel={paymentModel}
        incompleteStep={
          failedCreateStep !== undefined && stepErrors.has(failedCreateStep) ? failedCreateStep : undefined
        }
        onBack={() => setStep(step - 1)}
        onNext={() => {
          void handleNext();
        }}
        onCreate={() => {
          void handleCreate();
        }}
      />
      {voucherDialogNodes}
    </>
  );
};

export const CreateOrderWizard = ({
  editOrderDialogIntent,
  editOrderDialogState,
  closeEditOrderDialog,
  addOrder,
  voucherToRedeem,
  redeemVoucherCallback,
  waitingListItem,
  cloneSourceOrder,
  canToggleIntent,
  onToggleIntent,
}: CreateOrderWizardProps) => {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const language = useLanguage();
  const { executeWithConflictCheck } = useAssignmentConflictHandler();
  const { currentDate, calendarView } = useCalendar();
  // @ts-ignore
  const { editOrderState } = useEditOrder();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { parentId, parent, onSubmitCallback } = editOrderDialogState;

  const isEventOrCourse = includes(
    [OrderPageTypes.COURSE, OrderPageTypes.EVENT],
    editOrderState.orderPage.order_page_type,
  );
  const isCourse = editOrderState.orderPage.order_page_type === OrderPageTypes.COURSE;
  const isVoucherRedeem = !!voucherToRedeem;
  const { model: paymentModel } = usePaymentModel(isVoucherRedeem);
  const isWaitlistItem = !!waitingListItem;
  const isClone = !!cloneSourceOrder;

  // A copy starts from the same blank order as a new one; the source's comment, custom personal details and
  // resource option carry over (the customer comes through the edit order context).
  const order: Partial<OrderData> = {
    ...getInitialOrder(
      parent,
      !isEventOrCourse && !isWaitlistItem,
      [CalendarView.DAY, CalendarView.AREAS].includes(calendarView) ? currentDate : moment().format(DATE_FORMAT),
      voucherToRedeem,
      editOrderDialogState.slot,
    ),
    comment: cloneSourceOrder?.comment,
    custom_personal_details: cloneSourceOrder?.custom_personal_details,
    resource_option_id: cloneSourceOrder?.resource_option_id,
  };

  const [loading, setLoading] = useState(false);
  const entityKey = cloneSourceOrder?.id ?? waitingListItem?.id ?? voucherToRedeem?.id ?? 'new';

  const initialValues: EditOrderFormValues = useMemo(
    () =>
      buildOrderFormInitialValues({
        editOrderState,
        order,
        isEditOrder: false,
        isEventOrCourse,
        voucherToRedeem,
        waitingListItem,
        account,
        isPendingEditedOrder: false,
        dateOverride: isClone ? '' : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityKey],
  );

  const contactOptional = !!editOrderState.orderPage?.contact_details_optional_in_backoffice;
  const validationSchema = useMemo(
    () => buildOrderValidationSchema(t, { isOneOfVoucherIntents: false, contactOptional }),
    [t, contactOptional],
  );

  const { availableDatesForEvents, isLoadingDates } = useEditOrderDates({
    isEventOrCourse,
    accountId: editOrderState.account.id,
    orderPageId: editOrderState.orderPage.id,
    orderDetails: editOrderState.details,
    currentDate: initialValues.date,
  });

  const voucherDialogs = useVoucherDialogs({
    order,
    orderPage: editOrderState.orderPage,
    closeEditOrderDialog,
  });

  // A blank order opens on step 1; the others open on the first incomplete step. Event and course dates are not
  // loaded yet at that point, so for them only the date's presence is checked.
  const [initialStep] = useState<WizardStep>(() => {
    if (editOrderDialogIntent === EditOrderDialogIntent.CreateOrder) {
      return WizardStep.WhatWhen;
    }
    const stepOneError = isEventOrCourse
      ? initialValues.date
        ? undefined
        : StepOneError.MISSING_DATE
      : validateStepOne(initialValues, { isEventOrCourse, availableDatesForEvents: [] });
    const customerErrors = failingFieldNames(validationSchema, initialValues).filter(
      (field) => stepOfField(field) === WizardStep.Customer,
    );
    return firstIncompleteStep({ stepOneError, customerErrors });
  });

  const handleCreate = async (values: EditOrderFormValues) => {
    setLoading(true);
    let orderResp: OrderData;
    try {
      orderResp = await executeWithConflictCheck(
        () =>
          OrderService.createOrder(
            buildCreateOrderPayload({
              values,
              editOrderState,
              language,
              parentId,
              isEventOrCourse,
              isCourse,
              availableDatesForEvents,
              voucherToRedeem,
              waitingListItem,
              account,
              depositPrice: isVoucherRedeem
                ? undefined
                : depositPriceToSend({
                    model: paymentModel,
                    upfrontAmount: values.upfrontAmount,
                    orderPrice: editOrderState.details.price,
                    createPaymentLink: values.createPaymentLink,
                  }),
            }),
          ),
        '',
        () => setLoading(false),
        true,
      );
    } catch {
      // The error is already shown; the drawer stays open so nothing typed is lost.
      return;
    }
    // The order exists from here on: close first so a failing refetch can never leave the form open for a duplicate.
    if (orderResp.payment_link) {
      voucherDialogs.setPaymentLink(
        `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/payment-link?linkid=${orderResp.payment_link}`,
      );
    } else {
      closeEditOrderDialog();
    }
    posthog.capture('Order Created', { editor: 'admin', wizard: true });
    if (addOrder) await addOrder(orderResp);
    if (redeemVoucherCallback && voucherToRedeem) redeemVoucherCallback(voucherToRedeem);
    if (onSubmitCallback) onSubmitCallback();
  };

  return (
    <Formik<EditOrderFormValues>
      key={entityKey}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleCreate}>
      <CreateOrderWizardBody
        editOrderDialogIntent={editOrderDialogIntent}
        editOrderDialogState={editOrderDialogState}
        closeEditOrderDialog={closeEditOrderDialog}
        voucherToRedeem={voucherToRedeem}
        waitingListItem={waitingListItem}
        cloneSourceOrder={cloneSourceOrder}
        canToggleIntent={canToggleIntent}
        onToggleIntent={onToggleIntent}
        initialStep={initialStep}
        isEventOrCourse={isEventOrCourse}
        availableDatesForEvents={availableDatesForEvents}
        isLoadingDates={isLoadingDates}
        isWaitlistItem={isWaitlistItem}
        loading={loading}
        voucherDialogNodes={voucherDialogs.dialogNodes}
      />
    </Formik>
  );
};
